// ================================================= /
// ======   Interactive JS   ======================= /
// ================================================= /

/* eslint-disable no-magic-numbers, max-statements */

/* global jQuery */

import gsap from 'gsap';
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin';
import { MorphSVGPlugin } from 'gsap/MorphSVGPlugin';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';
import { SplitText } from 'gsap/SplitText';

gsap.registerPlugin( DrawSVGPlugin, MorphSVGPlugin, MotionPathPlugin, SplitText );

import animation from './animation';
import slider from './slider';

/**
 * Class for controlling the interactive element.
 */
class DispatchSkinInteractive {

	/**
	 * Constructs the class and sets up instance variables.
	 */
	constructor() {

		this.id = 'interactive';
		this.selector = '.js-interactive';

		this.interactive = null;

		this.transitionDuration = 0.25;

		this.tweens = {};
		this.resolvedTweens = {};

		this.timelines = [
			{ main: null, transition: [null, null, null, null] },
			{ main: null, transition: [null, null, null, null] },
			{ main: null, transition: [null, null, null, null] },
			{ main: null, transition: [null, null, null, null] }
		];

		this.currentTimeline = null;
		this.isTransitioning = false;
	}

	/**
	 * Initializes the class, sets up events.
	 */
	initialize() {

		this.interactive = jQuery( `#${this.id}` );

		if ( this.interactive.length ) {

			this.currentIndex = slider.getActiveIndex( this.interactive );

			// Set elements

			this.headingReferences = jQuery( `${this.selector}__heading-reference` );
			this.contentReferences = jQuery( `${this.selector}__content-reference` );
			this.heading = jQuery( `${this.selector}__heading` );
			this.content = jQuery( `${this.selector}__content` );

			this.dots = jQuery( `${this.selector}__dot` );
			this.icons = jQuery( `${this.selector}__icon` );
			this.lgIcons = this.icons.filter( `${this.selector}__icon--size-lg` );

			this.lgIconEllipsesContainers = this.lgIcons.find( `${this.selector}__icon-ellipses` );
			MorphSVGPlugin.convertToPath( this.lgIconEllipsesContainers.find( 'ellipse' ) );
			this.lgIconEllipses = this.lgIconEllipsesContainers.find( 'path' );
			this.iconInners = jQuery( `${this.selector}__icon-inner` );

			this.iconReferenceHexagon = jQuery( `${this.selector}__icon-reference-hexagon path` )[0];

			this.iconReferenceEllipse = jQuery( `${this.selector}__icon-reference-ellipse` );
			MorphSVGPlugin.convertToPath( this.iconReferenceEllipse.find( 'ellipse' ) );
			this.iconReferenceEllipse = this.iconReferenceEllipse.find( 'path' )[0];

			this.grid = jQuery( `${this.selector}__grid` );
			this.gridMain = jQuery( `${this.selector}__grid-main` );
			this.gridMask = jQuery( `${this.selector}__grid-mask` );

			this.connectorMain = jQuery( `${this.selector}__connector-main` );
			this.connectorMask = jQuery( `${this.selector}__connector-mask` );

			this.enterprisePulses = jQuery( `${this.selector}__enterprise .o-svg:not(:first-child)` );

			this.triangleOverlay = jQuery( `${this.selector}__triangle-overlay` );
			this.graphOverlay = jQuery( `${this.selector}__graph-overlay` );

			this.triangleLines = jQuery( `${this.selector}__triangle-line` );
			this.triangleOuterLines = jQuery( `${this.selector}__triangle-outer-line` );
			this.triangleInnerLines = jQuery( `${this.selector}__triangle-inner-line` );

			this.calendars = jQuery( `${this.selector}__calendar` );
			this.truck = jQuery( `${this.selector}__truck` );
			this.bells = jQuery( `${this.selector}__bell` );
			this.stars = jQuery( `${this.selector}__star` );

			this.graph = jQuery( `${this.selector}__graph` );
			this.graphLines = jQuery( `${this.selector}__graph-lines` );
			this.graphLinesMain = jQuery( `${this.selector}__graph-lines-main` );
			this.graphLinesMask = jQuery( `${this.selector}__graph-lines-mask` );

			this.lineGraphs = jQuery( `${this.selector}__line-graph` );
			this.reportLabels = jQuery( `${this.selector}__report-label` );

			this.setTweens();
			this.setTimelines();
			this.events();
		}
	}

	/**
	 * Wires up events
	 */
	events() {

		// Slider hooks

		slider.addHook(
			'beforeTouch',
			this.selector,
			() => false
		);
		slider.addHook(
			'beforeChangeSlide',
			this.selector,
			function( targetSlider, toIndex ) {
				return ! this.isTransitioning && slider.getActiveIndex( targetSlider ) !== toIndex;
			}.bind( this )
		);
		slider.addHook(
			'beforeChangeIndex',
			this.selector,
			this.handleStageChange.bind( this )
		);
		slider.addHook(
			'beforeAnimateChangeSlide',
			this.selector,
			() => false
		);

		// Animation hooks

		animation.addHook(
			this.selector,
			() => this.handleStageChange( this.interactive, 0 )
		);

	}

	/**
	 * Handles the stage change.
	 */
	handleStageChange( interactive, toIndex ) {

		this.isTransitioning = true;
		this.toIndex = toIndex;

		// Change the text

		const textElements = [
			{
				name: 'heading',
				delay: 0,
				duration: 1.5
			},
			{
				name: 'content',
				duration: 2,
				delay: 0.1
			}
		];

		for ( let i = 0; i < textElements.length; i++ ) {

			this.changeText(
				this[textElements[i].name],
				this[textElements[i].name + 'References'].eq( toIndex ).text(),
				textElements[i].delay * i,
				textElements[i].stagger / ( ( i + 1 ) * 2 )
			);
		}

		// If there is no timeline currently being played, immediately null the transition

		if ( ! this.currentTimeline ) {

			this.resetIsTransitioning();
		}

		this.currentTimeline = this.getNextTimeline( this.currentIndex, this.toIndex )
			.invalidate()
			.restart();

		this.currentIndex = toIndex;
	}

	/* ------------------------------------------------------ */
	/* Handling tweens -------------------------------------- */
	/* ------------------------------------------------------ */

	/**
	 * Sets tweens.
	 */
	setTweens() {

		const dashOffset = 32;
		const gridDrawInDuration = 1.5;
		const gridDrawOutDuration = 0.5;
		const danceDuration = 3.5;

		const pulseDuration = 1.8;
		const pulseScale = 1.8;

		const triangleDashedLineDuration = 5.5;

		const lgIconJumpDuration = 0.125;
		const lgIconJumpScale = 1.15;

		const getTargetPathLength = ( index, target ) => DrawSVGPlugin.getLength( target ) * -1;

		/* ----------------------------------- */
		/* Dots & icons ---------------------- */

		this.addTween( 'dotsIn', this.dots, { stagger: { amount: 0.75, from: 'center' }, opacity: 1, y: 0, scale: 1 } );
		this.addTween(
			'dotsOut',
			this.dots,
			{ stagger: { amount: 0.75, from: 'center' }, opacity: 0, y: 0, scale: 0, onComplete: () => gsap.set( this.dots, { clearProps: 'all' } ), callbackScope: this }
		);
		this.addTween( 'dotsStop', this.dots, { duration: 0.75, x: 0, y: 0, overwrite: 'auto' } );

		this.addTween( 'iconsIn', this.icons, { stagger: { amount: 0.2, from: 'center' }, opacity: 1, scale: 1 } );
		this.addTween( 'smIconsOut', this.icons.not( this.lgIcons ), { ease: 'back.inOut', stagger: { amount: 0.2, from: 'center' }, opacity: 0, scale: 0 } );
		this.addTween(
			'lgIconsEmbiggen',
			this.lgIcons,
			{
				duration: 0.35,
				scale: 1.78787878787,
				overwrite: 'auto',
				callbackScope: this,
				onStart: () => this.lgIconEllipsesContainers.addClass( 'is-morphed' )
			}
		);
		this.addTween(
			'lgIconsEmsmallen',
			this.lgIcons,
			{
				duration: 0.25,
				scale: 1,
				overwrite: 'auto',
				callbackScope: this,
				onStart: () => this.lgIconEllipsesContainers.removeClass( 'is-morphed' )
			}
		);

		this.addTween( 'lgIconsMorphIn', this.lgIconEllipses, { duration: 0.25, morphSVG: this.iconReferenceHexagon } );
		this.addTween( 'lgIconsMorphOut', this.lgIconEllipses, { duration: 0.25, morphSVG: this.iconReferenceEllipse } );
		this.addTween(
			'lgIconsFirstPulse',
			this.lgIconEllipses.filter( ':nth-child(2)' ),
			{ duration: pulseDuration, ease: 'none', repeat: -1, opacity: 0, scale: pulseScale }
		);
		this.addTween(
			'lgIconsSecondPulse',
			this.lgIconEllipses.filter( ':last-child' ),
			{ duration: pulseDuration, delay: pulseDuration / 2, ease: 'none', repeat: -1, opacity: 0, scale: pulseScale }
		);
		this.addTween( 'lgIconsPulseOut', this.lgIconEllipses.not( ':first-child' ), { duration: 0.15, scale: 1, overwrite: 'auto' } );

		/* ----------------------------------- */
		/* The grid -------------------------- */

		this.addTween( 'gridShow', this.grid, { duration: 0.1, opacity: 1 }, );
		this.addTween( 'gridHide', this.grid, { duration: 0.1, opacity: 0 }, );
		this.addTween( 'gridIn', this.gridMask.find( 'path, polyline, line' ), { duration: gridDrawInDuration, drawSVG: '100% 100% live' } );
		this.addTween( 'gridOut', this.gridMask.find( 'path, polyline, line' ), { duration: gridDrawOutDuration, drawSVG: '0 100% live' } );
		this.addTween( 'gridDance', this.gridMain.find( 'path, polyline, line' ), { duration: danceDuration, ease: 'none', strokeDashoffset: '-=' + dashOffset, repeat: -1 } );

		this.addTween( 'connectorIn', this.connectorMask.find( 'line' ), { duration: gridDrawInDuration, drawSVG: '100% 100% live' } );
		this.addTween( 'connectorDance', this.connectorMain, { duration: danceDuration, ease: 'none', strokeDashoffset: '-=' + dashOffset, repeat: -1 } );
		this.addTween( 'connectorOut', this.connectorMask.find( 'line' ), { duration: gridDrawOutDuration, drawSVG: '0 100% live' } );

		/* ----------------------------------- */
		/* Triangle -------------------------- */

		this.addTween( 'triangleOverlayIn', this.triangleOverlay, { duration: 0.5, opacity: 0.5 } );
		this.addTween( 'triangleOverlayOut', this.triangleOverlay, { duration: 0.5, opacity: 0 } );

		this.addTween( 'triangleLinesShow', this.triangleLines, { duration: 0.05, opacity: 1 } );
		this.addTween( 'triangleLinesHide', this.triangleLines, { duration: 0.05, opacity: 0 } );
		this.addTween( 'triangleLinesIn', this.triangleLines, { duration: 0.65, ease: 'power3.inOut', stagger: 0.5, drawSVG: '0 100% live' } );
		this.addTween( 'triangleLinesOut', this.triangleLines, { duration: 0.25, ease: 'power3.inOut', stagger: 0.25, drawSVG: '0 0 live' } );

		this.addTween( 'triangleOuterLinesShow', this.triangleOuterLines, { duration: 0.05, opacity: 1 } );
		this.addTween( 'triangleOuterLinesIn',
			this.triangleOuterLines,
			{
				duration: triangleDashedLineDuration,
				ease: 'none',
				stagger: { each: 0.5, repeat: -1 },
				strokeDashoffset: getTargetPathLength
			}
		);

		this.addTween( 'triangleInnerLinesShow', this.triangleInnerLines, { duration: 0.05, opacity: 1 } );
		this.addTween(
			'triangleInnerLinesIn',
			this.triangleInnerLines,
			{
				duration: triangleDashedLineDuration,
				ease: 'none',
				stagger: { each: 0.5, repeat: -1 },
				strokeDashoffset: getTargetPathLength
			}
		);

		this.addTween( 'triangleInnerOuterLinesOut',
			[this.triangleInnerLines, this.triangleOuterLines],
			{
				duration: 0.1,
				opacity: 0,
				onComplete: function() {
					this.getTween( 'triangleOuterLinesIn' ).pause( 0 );
					this.getTween( 'triangleInnerLinesIn' ).pause( 0 );
					gsap.set( [this.triangleInnerLines, this.triangleOuterLines], { opacity: 0 } );
				}.bind( this )
			}
		);

		// Calendars

		const calendarOffsets = [
			{ x: '-288%', y: 0 },
			{ x: '119%', y: '-170%' },
			{ x: '280%', y: '119%' }
		];

		this.addTween(
			'calendarsIn',
			this.calendars,
			{ duration: 0.8, x: ( i ) => calendarOffsets[i].x, y: ( i ) => calendarOffsets[i].y, scale: 1, rotate: 0 }
		);

		this.addTween(
			'calendarsOut',
			this.calendars,
			{ duration: 0.25, scale: 0, overwrite: 'auto', onComplete: () => gsap.set( this.calendars, { clearProps: 'all' } ), callbackScope: this }
		);

		const calendars = 3;
		const calendarDuration = 3;
		const calendarStagger = 0.2;

		for ( let i = 0; i < calendars; i++ ) {

			this.addTween(
				'calendar' + i + 'To',
				this.calendars.eq( i ),
				{
					duration: calendarDuration + calendarStagger * i,
					ease: 'power2.out',
					motionPath: {
						path: `${this.selector}__calendar-path-${i}`,
						align: `${this.selector}__calendar-path-${i}`,
						autoRotate: -90
					}
				}
			);
		}

		// The jumpy icon

		this.addTween(
			'lgTruckIconJump',
			[
				this.lgIcons.eq( 0 ).find( this.lgIconEllipses )
					.eq( 0 ),
				this.lgIcons.eq( 0 ).find( this.iconInners )
			],
			{
				duration: lgIconJumpDuration,
				delay: calendarDuration - 0.8,
				scale: lgIconJumpScale,
				repeat: calendars * 2 - 1,
				yoyo: true
			}
		);

		// Truck

		this.addTween( 'truckIn', this.truck, { duration: 0.1, scale: 1 } );
		this.addTween( 'truckTo', this.truck, { duration: 5, ease: 'none', x: '3370%' } );
		this.addTween(
			'truckOut',
			this.truck,
			{ duration: 0.25, scale: 0, overwrite: 'auto', onComplete: () => gsap.set( this.truck, { clearProps: 'all' } ), callbackScope: this }
		);

		// Bells

		const bellOffsets = [
			{ x: '-308%', y: '-229%' },
			{ x: '-209%', y: '150%' },
			{ x: '270%', y: '199%' }
		];

		this.addTween(
			'bellsIn',
			this.bells,
			{ duration: 0.8, x: ( i ) => bellOffsets[i].x, y: ( i ) => bellOffsets[i].y, scale: 1, rotate: 0 }
		);

		this.addTween(
			'bellsRing',
			this.bells,
			{ duration: 0.15, rotation: '40deg', repeat: 1, yoyo: true }
		);

		this.addTween(
			'bellsOut',
			this.bells,
			{ duration: 0.25, scale: 0, overwrite: 'auto', onComplete: () => gsap.set( this.bells, { clearProps: 'all' } ), callbackScope: this }
		);

		const bells = 3;
		const bellDuration = 2.75;
		const bellStagger = 0.25;

		for ( let i = 0; i < bells; i++ ) {

			this.addTween(
				'bell' + i + 'To',
				this.bells.eq( i ),
				{
					duration: bellDuration + bellStagger * i,
					ease: 'power1.out',
					motionPath: {
						path: `${this.selector}__bell-path-${i}`,
						align: `${this.selector}__bell-path-${i}`,
						autoRotate: 40
					}
				}
			);
		}

		// The jumpy icon

		this.addTween(
			'lgHouseIconJump',
			[
				this.lgIcons.eq( 1 ).find( this.lgIconEllipses )
					.eq( 0 ),
				this.lgIcons.eq( 1 ).find( this.iconInners )
			],
			{
				duration: lgIconJumpDuration,
				delay: bellDuration - 0.25,
				scale: lgIconJumpScale,
				repeat: bells * 2 - 1,
				yoyo: true
			}
		);

		// The stars that fall into the sea

		this.addTween( 'starsIn', this.stars, { duration: 0.075, stagger: { amount: 0.75 }, scale: 1 } );

		const starDuration = 2.25;
		const starStagger = 0.015;

		this.addTween(
			'starsTo',
			this.stars,
			{
				duration: starDuration,
				ease: 'back.in(0.075)',
				stagger: starStagger,

				x: function( index, target ) {
					return this.getPercentageBasedTransformBetweenElements( target, index & 1 ? this.lgIcons.eq( 0 )[0] : this.enterprisePulses[0] ).x + '%';
				}.bind( this ),

				y: function( index, target ) {
					return this.getPercentageBasedTransformBetweenElements( target, index & 1 ? this.lgIcons.eq( 0 )[0] : this.enterprisePulses[0] ).y + '%';
				}.bind( this )
			}
		);

		this.addTween(
			'starsOut',
			this.stars,
			{ duration: 0.25, scale: 0, overwrite: 'auto', onComplete: () => gsap.set( this.stars, { clearProps: 'all' } ), callbackScope: this }
		);

		/* ----------------------------------- */
		/* Enterprise ------------------------ */

		this.addTween(
			'enterprisePulse',
			this.enterprisePulses,
			{ duration: pulseDuration,	ease: 'none', stagger: { each: pulseDuration / 2, repeat: -1 },	opacity: 0, scale: pulseScale }
		);

		/* ----------------------------------- */
		/* Graph ----------------------------- */

		const lineGraphDrawDuration = 3.5;

		this.addTween( 'graphOverlayIn', this.graphOverlay, { duration: 0.25, opacity: 1 } );
		this.addTween( 'graphOverlayOut', this.graphOverlay, { duration: 0.25, opacity: 0 } );

		this.addTween( 'graphShow', [this.graph, this.graphLines], { duration: 0.1, opacity: 1 } );
		this.addTween( 'graphHide', [this.graph, this.graphLines], { duration: 0.1, opacity: 0 } );

		this.addTween( 'graphLinesIn', this.graphLinesMask.find( 'path' ), { duration: gridDrawInDuration, drawSVG: '100% 100% live' } );
		this.addTween( 'graphLinesOut', this.graphLinesMask.find( 'path' ), { duration: gridDrawOutDuration, drawSVG: '0 100% live' } );

		this.addTween( 'graphLinesDance', this.graphLinesMain.find( 'path' ), { duration: danceDuration, ease: 'none', strokeDashoffset: '-=' + dashOffset, repeat: -1 } );

		this.addTween( 'reportLabelsIn', this.reportLabels, { duration: 0.55, stagger: 0.2, opacity: 1, y: 0 } );
		this.addTween(
			'reportLabelsOut',
			this.reportLabels,
			{ duration: 0.15, opacity: 0, y: '25%', onComplete: () => gsap.set( this.reportLabels, { clearProps: 'all' } ), callbackScope: this }
		);

		this.addTween( 'graph1LineGraphIn', this.lineGraphs.eq( 0 ).find( 'polyline' ), { duration: lineGraphDrawDuration, ease: 'none', drawSVG: '0 100% live' } );
		this.addTween( 'graph1CirclesIn', this.lineGraphs.eq( 0 ).find( 'circle' ), { duration: 0.25, stagger: 0.55, ease: 'back.out', scale: 1 } );
		this.addTween( 'graph1ArrowsIn', this.lineGraphs.eq( 0 ).find( 'line' ), { duration: 0.25, stagger: 0.1, opacity: 1, drawSVG: '0 100% live' } );

		this.addTween( 'graph2LineGraphIn', this.lineGraphs.eq( 1 ).find( 'polyline' ), { duration: lineGraphDrawDuration, ease: 'none', drawSVG: '0 100% live' } );
		this.addTween( 'graph2CirclesIn', this.lineGraphs.eq( 1 ).find( 'circle' ), { duration: 0.25, stagger: 0.55, ease: 'back.out', scale: 1 } );
		this.addTween( 'graph2ArrowsIn', this.lineGraphs.eq( 1 ).find( 'line' ), { duration: 0.25, stagger: 0.1, opacity: 1, drawSVG: '0 100% live' } );

		this.addTween( 'graphLineGraphsOut', this.lineGraphs.find( 'polyline' ), { duration: 0.5, ease: 'none', drawSVG: '0 0 live' } );
		this.addTween( 'graphCirclesOut', this.lineGraphs.find( 'circle' ), { duration: 0.25, ease: 'back.in', scale: 0 } );
		this.addTween(
			'graphArrowsOut',
			this.lineGraphs.find( 'line' ),
			{ duration: 0.1, opacity: 0, drawSVG: '0 0 live' }
		);
	}

	/**
	 * Adds a tween.
	 */
	addTween( name, targets, props, type = 'to' ) {

		this.tweens[name] = {
			type: type,
			targets: targets,
			props: props
		};
	}

	/**
	 * Gets a tween from stored props.
	 */
	getTween( name, force = false ) {

		if ( ! this.resolvedTweens[name] || force ) {

			const tween = this.tweens[name];
			const tweenObject = gsap[tween.type]( tween.targets, tween.props );

			this.resolvedTweens[name] = tweenObject;
		}

		return this.resolvedTweens[name];
	}

	/* ------------------------------------------------------ */
	/* Handling timelines ----------------------------------- */
	/* ------------------------------------------------------ */

	/**
	 * Sets the timeline.
	 */
	setTimelines() {

		/* ----------------------------------- */
		/* Initial state --------------------- */

		const initialMain = gsap.timeline( { paused: true } );

		initialMain
			.call( this.resetIsTransitioning.bind( this ) )
			.add( this.getTween( 'dotsIn' ) )
			.add( this.getTween( 'iconsIn' ), '<0.25' )
			.addLabel( 'dotWiggle' );

		for ( let i = 0; i < this.dots.length; i++ ) {
			initialMain.to(
				this.dots.eq( i ),
				{
					duration: Math.max( 5, Math.random() * 10 ),
					x: 'random(-10, 10)',
					y: 'random(-10, 10)',
					repeat: -1,
					yoyo: true,
					overwrite: 'auto'
				},
				i === 0 ? '>' : '<'
			);
		}

		this.timelines[0].main = initialMain;

		// Transition out

		const initialOut = gsap.timeline( { paused: true } )
			.call( this.transitionToNextMain.bind( this ) )
			.add( this.getTween( 'dotsStop' ) )
			.add( this.getTween( 'enterprisePulse' ), '<' )
			.call( this.resetIsTransitioning.bind( this ), [], '<' );

		this.timelines[0].transition[1] = initialOut;
		this.timelines[0].transition[2] = initialOut;
		this.timelines[0].transition[3] = initialOut;


		/* ----------------------------------- */
		/* Stage 1 --------------------------- */

		const stageOneMain = gsap.timeline( { paused: true } );

		stageOneMain
			.add( this.getTween( 'gridShow' ), '<0.125' )
			.add( this.getTween( 'gridIn' ), '<' )
			.call(
				function() {
					this.getTween( 'gridDance' ).resume();
				}.bind( this ),
				[],
				'<'
			)
			.add( this.getTween( 'connectorIn' ), '<' )
			.call(
				function() {
					this.getTween( 'connectorDance' ).resume();
				}.bind( this ),
				[],
				'<'
			);

		this.timelines[1].main = stageOneMain;

		// Transition out

		const stageOneOut = gsap.timeline( { paused: true } )
			.call( this.transitionToNextMain.bind( this ) )
			.add( this.getTween( 'connectorOut' ), '<' )
			.call(
				function() {
					this.getTween( 'connectorDance' ).pause();
				}.bind( this ),
				[],
				'<'
			)
			.add( this.getTween( 'gridOut' ), '<' )
			.add( this.getTween( 'gridHide' ) )
			.call(
				function() {
					this.getTween( 'gridDance' ).pause();
				}.bind( this ),
				[],
				'<'
			)
			.call( this.resetIsTransitioning.bind( this ) );

		this.timelines[1].transition[2] = stageOneOut;
		this.timelines[1].transition[3] = stageOneOut;

		/* ----------------------------------- */
		/* Stage 2 --------------------------- */

		const stageTwoLoop = gsap.timeline( { paused: true } )
			.add( this.getTween( 'calendarsIn' ) )
			.addLabel( 'calendars', '>0.3' )
			.add( this.getTween( 'calendar0To' ), 'calendars' )
			.add( this.getTween( 'calendar1To' ), 'calendars' )
			.add( this.getTween( 'calendar2To' ), 'calendars' )
			.add( this.getTween( 'calendarsOut' ) )
			.add( this.getTween( 'lgTruckIconJump' ), 'calendars' )
			.add( this.getTween( 'truckIn' ), '>0.15' )
			.addLabel( 'truck' )
			.add( this.getTween( 'truckTo' ), '<' )
			.add( this.getTween( 'truckOut' ) )
			.addLabel( 'bellsMove', 'truck+=1.5' )
			.add( this.getTween( 'bellsIn' ), 'truck-=0.25' )
			.add( this.getTween( 'bellsRing' ), '>' )
			.add( this.getTween( 'bell0To' ), 'bellsMove' )
			.add( this.getTween( 'bell1To' ), 'bellsMove' )
			.add( this.getTween( 'bell2To' ), 'bellsMove' )
			.add( this.getTween( 'bellsOut' ) )
			.add( this.getTween( 'lgHouseIconJump' ), 'bellsMove' )
			.add( this.getTween( 'starsIn' ), '<0.25' )
			.add( this.getTween( 'starsTo' ) )
			.add( this.getTween( 'starsOut' ) )
			.call(
				function() {
					if ( this.currentIndex === 2 && ! this.isTransitioning ) {
						stageTwoLoop.restart();
					}
				}.bind( this ),
				[],
				'>2'
			);

		const stageTwoMain = gsap.timeline( { paused: true } )
			.add( this.getTween( 'smIconsOut' ) )
			.add( this.getTween( 'lgIconsEmbiggen' ), '<' )
			.add( this.getTween( 'lgIconsMorphIn' ), '<' )
			.add( this.getTween( 'lgIconsFirstPulse' ), '<' )
			.add( this.getTween( 'lgIconsSecondPulse' ), '<' )
			.add( this.getTween( 'triangleOverlayIn' ), '<' )
			.add( this.getTween( 'triangleLinesShow' ), '<' )
			.add( this.getTween( 'triangleLinesIn' ), '<' )
			.add( this.getTween( 'triangleOuterLinesShow' ), '<' )
			.call(
				function() {
					this.getTween( 'triangleOuterLinesIn' ).resume();
				}.bind( this ),
				[],
				'<0.25'
			)
			.add( this.getTween( 'triangleInnerLinesShow' ), '<' )
			.call(
				function() {
					this.getTween( 'triangleInnerLinesIn' ).resume();
				}.bind( this ),
				[],
				'<'
			)
			.call( () => stageTwoLoop.restart(), [], '<1' );

		this.timelines[2].main = stageTwoMain;

		// Transition out

		const stageTwoOut = gsap.timeline( { paused: true } )
			.call( this.transitionToNextMain.bind( this ) )
			.call(
				function() {
					stageTwoLoop.tweenTo( stageTwoLoop.nextLabel() ).duration( 1 );
				}
			)
			.call(
				function() {
					this.getTween( 'iconsIn', true );
				}.bind( this ),
				[],
				'<'
			)
			.add( this.getTween( 'lgIconsEmsmallen' ), '<' )
			.add( this.getTween( 'lgIconsMorphOut' ), '<' )
			.add( this.getTween( 'lgIconsPulseOut' ), '<' )
			.add( this.getTween( 'triangleOverlayOut' ), '<' )
			.add( this.getTween( 'triangleLinesOut' ), '<' )
			.add( this.getTween( 'triangleLinesHide' ), '<' )
			.add( this.getTween( 'triangleInnerOuterLinesOut' ), '<' )
			.add( this.getTween( 'calendarsOut' ) )
			.add( this.getTween( 'truckOut' ), '<' )
			.add( this.getTween( 'bellsOut' ), '<' )
			.add( this.getTween( 'starsOut' ), '<' )
			.call( this.resetIsTransitioning.bind( this ) );

		this.timelines[2].transition[1] = stageTwoOut;
		this.timelines[2].transition[3] = stageTwoOut;

		/* ----------------------------------- */
		/* Stage 3 --------------------------- */

		const stageThreeMain = gsap.timeline( { paused: true } )
			.add( this.getTween( 'graphOverlayIn' ) )
			.add( this.getTween( 'graphShow' ), '>' )
			.add( this.getTween( 'graphLinesIn' ), '<' )
			.add( this.getTween( 'reportLabelsIn' ), '<1' )
			.call(
				function() {
					this.getTween( 'graphLinesDance' ).resume();
				}.bind( this ),
				[],
				'<'
			)
			.addLabel( 'lineGraphs' )
			.add( this.getTween( 'graph1CirclesIn' ), '<0.5' )
			.add( this.getTween( 'graph1LineGraphIn' ), '<' )
			.add( this.getTween( 'graph1ArrowsIn' ), '>' )
			.add( this.getTween( 'graph2CirclesIn' ), 'lineGraphs-=2' )
			.add( this.getTween( 'graph2LineGraphIn' ), '<' )
			.add( this.getTween( 'graph2ArrowsIn' ), '>' );

		this.timelines[3].main = stageThreeMain;

		// Transition out

		const stageThreeOut = gsap.timeline( { paused: true } )
			.call( stageThreeMain.pause )
			.call( this.transitionToNextMain.bind( this ) )
			.add( this.getTween( 'reportLabelsOut' ), '<' )
			.add( this.getTween( 'graphArrowsOut' ), '<' )
			.add( this.getTween( 'graphCirclesOut' ), '<' )
			.add( this.getTween( 'graphLineGraphsOut' ), '<' )
			.add( this.getTween( 'graphLinesOut' ), '<' )
			.add( this.getTween( 'graphHide' ) )
			.call(
				function() {
					this.getTween( 'graphLinesDance' ).pause();
				}.bind( this ),
				[],
				'>'
			)
			.add( this.getTween( 'graphOverlayOut' ) )
			.call( this.resetIsTransitioning.bind( this ) );

		this.timelines[3].transition[1] = stageThreeOut;
		this.timelines[3].transition[2] = stageThreeOut;
	}

	/**
	 * Changes text
	 */
	changeText( textElement, newText, props = {} ) {

		props = Object.assign(
			{ duration: 0.175, delay: 0 },
			props
		);

		const translate = '2.5rem';

		const splitElement = new SplitText(
			textElement,
			{ type: 'chars,lines' }
		);

		gsap.to(
			splitElement.chars,
			{
				delay: props.delay,
				stagger: {
					amount: props.duration,
					from: 'end'
				},
				x: translate,
				opacity: 0,
				onComplete: function() {

					gsap.set( textElement, { opacity: 0 } );
					splitElement.revert();
					textElement.text( newText );
					splitElement.split();

					gsap.set( splitElement.chars, { opacity: 0, x: translate } );
					gsap.set( textElement, { opacity: 1 } );

					gsap.to(
						splitElement.chars,
						{
							stagger: {	amount: props.duration },
							opacity: 1,
							x: 0,
							onComplete: function() {
								splitElement.revert();
								animation.setSectionOffsets();
							}
						}
					);
				}
			}
		);
	}

	/**
	 * Gets the next timeline to transition to.
	 */
	getNextTimeline( currentIndex, toIndex ) {

		// See if the current index has a timeline that transitions it to the next index
		if ( currentIndex !== -1 && this.timelines[currentIndex].transition[toIndex] ) {

			return this.timelines[currentIndex].transition[toIndex];
		}

		// Otherwise, we just get the main
		return this.timelines[toIndex].main;
	}

	/**
	 * Resets the transitioning global/
	 */
	resetIsTransitioning() {

		this.isTransitioning = false;
	}

	/**
	 * Transitions to the next main timeline after a transition one.
	 */
	transitionToNextMain() {

		this.currentTimeline = this.timelines[this.toIndex].main
			.invalidate()
			.restart();
	}

	/**
	 * Gets a callback returning the percentage-based transform value between two elements.
	 */
	getPercentageBasedTransformBetweenElements( targetElement, destinationElement ) {

		const targetRect = targetElement.getBoundingClientRect();
		const destinationRect = destinationElement.getBoundingClientRect();

		return {
			x: ( destinationRect.left + destinationRect.width / 2 - ( targetRect.left + targetRect.width / 2 ) ) / targetRect.width * 100,
			y: ( destinationRect.top + destinationRect.height / 2 - ( targetRect.top + targetRect.height / 2 ) ) / targetRect.height * 100
		};
	}

}

const interactive = new DispatchSkinInteractive();
export default interactive;



// WEBPACK FOOTER //
// ./src/js/inc/interactive.js